import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import Media from 'react-media';
import mediaQueries from '../../../utils/mediaQueries';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => backgroundColor};
  height: 624px;
  padding-top: 30px;
  padding-bottom: 90px;
  @media ${mediaQueries.mdUp} {
    height: 548px;
  }
  @media ${mediaQueries.lgPlusUp} {
    height: 630px;
  }
  @media ${mediaQueries.xlPlusUp} {
    height: 404px;
    padding-top: 34px;
    padding-bottom: 40px;
  }
`;
const Content = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: repeat(3, max-content);
  justify-items: center;
  height: 100%;
  align-content: center;
  @media ${mediaQueries.xlPlusUp} {
    grid-template-areas:
      'title image'
      'description image';
    grid-template-columns: max-content max-content;
    grid-template-rows: repeat(2, max-content);
    column-gap: ${({ columnGap }) => columnGap};
    justify-items: start;
    justify-content: center;
    align-content: center;
    padding: 0 106px;
  }
`;
const Title = styled.h3`
  color: ${({ theme }) => theme.colors.white};
  font-size: 28px;
  font-weight: bold;
  line-height: 38px;
  text-align: center;
  @media ${mediaQueries.xlPlusUp} {
    grid-area: title;
    font-size: 40px;
    line-height: 54px;
    text-align: left;
    align-self: end;
    position: relative;
    bottom: ${({ marginBottomTitle }) => marginBottomTitle};
    ${({ isSpanish }) => !isSpanish && `
      br {
        display: none;
      }
    `}
  }
`;
const ImageContainer = styled.div`
  width: ${({ size }) => size};
  margin-top: 50px;
  touch-action: none;
  @media ${mediaQueries.xlPlusUp} {
    grid-area: image;
    margin-top: 0;
    justify-self: end;
  }
`;
const Image = styled.img`
  width: 100%;
`;
const DescriptionContainer = styled.div`
  margin-top: 45px;
  padding-left: 38px;
  padding-right: 38px;
  touch-action: none;
  @media ${mediaQueries.xlPlusUp} {
    grid-area: description;
    margin-top: 0;
    max-width: 560px;
    padding-left: 0;
    padding-right: 0;
  }
`;
const DescriptionText = styled.p`
  color: ${({ theme }) => theme.colors.white};
  font-size: 19px;
  font-weight: 600;
  line-height: 26px;
  text-align: center;
  margin: 0;
  &:nth-child(2) {
    margin-top: 12px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 18px;
    line-height: 25px;
    text-align: left;
  }
`;

const Feature = ({ feature }) => {
  const intl = useIntl();
  return (
    <Media queries={mediaQueries}>
      {(matches) => (
        <Container backgroundColor={feature.backgroundColor}>
          <Content columnGap={feature.columnGap}>
            <Title
              marginBottomTitle={feature.marginBottomTitle}
              isSpanish={intl.locale === 'es'}
            >
              {feature.title}
            </Title>
            <ImageContainer
              size={
                matches.lgPlusDownLess
                  ? feature.size.mobile
                  : feature.size.desktop
              }
            >
              <Image src={feature.image} alt={feature.title} />
            </ImageContainer>
            <DescriptionContainer>
              <DescriptionText>{feature.description}</DescriptionText>
              {feature.description2 && (
                <DescriptionText>{feature.description2}</DescriptionText>
              )}
            </DescriptionContainer>
          </Content>
        </Container>
      )}
    </Media>
  );
};

Feature.propTypes = {
  feature: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    description2: PropTypes.string,
    image: PropTypes.string.isRequired,
    size: PropTypes.shape({
      mobile: PropTypes.string.isRequired,
      desktop: PropTypes.string.isRequired,
    }),
    backgroundColor: PropTypes.string.isRequired,
    columnGap: PropTypes.string.isRequired,
    marginBottomTitle: PropTypes.string.isRequired,
  }).isRequired,
};

export default Feature;
